import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Filter,
  History,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Upload,
  UserPlus,
  XCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { PageTitle } from '../../../../_metronic/layout/core';
import { fetchMetasUnilever, fetchMetasUnileverHistory, saveMetasUnilever } from './api';
import { FormModal } from './FormModal';
import { HistoryModal } from './history-modal';
import { ImportModal } from './ImportModal';
import { MetasUnileverTable } from './MetasUnileverTable';
import { NoticeModal } from './NoticeModal';
import { HistoryEntry, MetaUnileverRow } from './types';
import {
  createEmptyRow,
  EXPECTED_HEADERS,
  formatCurrencyBRL,
  formatMeta,
  getChangedRows,
  mergeRowsByDistributor,
  normalizeKey,
  parseMetaNumber,
  prepareRowsForSave,
  sanitizeMetaInput,
  skippedRowLines,
  validationErrorLines,
} from './utils';

type StatusFilter = 'all' | 'pending' | 'unchanged';

type NoticeState = {
  title: string;
  message?: string;
  details?: string[];
  variant?: 'success' | 'danger' | 'info' | 'confirm';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
};

function hydrateRows(rows: MetaUnileverRow[]) {
  return rows.map((row) => ({
    ...row,
    metaHpf: formatMeta(row.metaHpf),
  }));
}

const MetasUnileverScreen: React.FC = () => {
  const [rows, setRows] = useState<MetaUnileverRow[]>([]);
  const [originalRows, setOriginalRows] = useState<MetaUnileverRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [regionalFilter, setRegionalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [minMetaFilter, setMinMetaFilter] = useState('');
  const [maxMetaFilter, setMaxMetaFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const rowsRef = useRef<MetaUnileverRow[]>([]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const closeNotice = useCallback(() => {
    if (noticeLoading) return;
    setNotice(null);
  }, [noticeLoading]);

  const openNotice = useCallback((nextNotice: NoticeState) => {
    setNotice(nextNotice);
  }, []);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchMetasUnilever();
      if (!response.success) {
        openNotice({
          title: 'Nao foi possivel carregar',
          message: response.error || 'Tente novamente em alguns instantes.',
          variant: 'danger',
        });
        return;
      }

      const hydratedRows = hydrateRows(response.data?.rows || []);
      setRows(hydratedRows);
      setOriginalRows(hydratedRows);
    } catch {
      openNotice({
        title: 'Nao foi possivel carregar',
        message: 'Falha na comunicacao com o servidor.',
        variant: 'danger',
      });
    } finally {
      setIsLoading(false);
    }
  }, [openNotice]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const originalGuidSet = useMemo(() => new Set(originalRows.map((row) => row.guid)), [originalRows]);
  const currentGuidSet = useMemo(() => new Set(rows.map((row) => row.guid)), [rows]);
  const changedRows = useMemo(() => getChangedRows(rows, originalRows), [rows, originalRows]);
  const removedCount = useMemo(
    () => originalRows.filter((row) => !currentGuidSet.has(row.guid)).length,
    [originalRows, currentGuidSet]
  );
  const pendingCount = changedRows.length + removedCount;
  const changedGuids = useMemo(() => new Set(changedRows.map((row) => row.guid)), [changedRows]);
  const regionalOptions = useMemo(() => {
    const uniqueRegionals = new Set<string>();
    rows.forEach((row) => {
      if (row.regional) uniqueRegionals.add(row.regional);
    });

    return Array.from(uniqueRegionals).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);
  const indexedRows = useMemo(() => {
    return rows.map((row) => ({
      row,
      searchKey: normalizeKey([
        row.distribuidor,
        row.consultor,
        row.coordenador,
        row.executivoBpc,
        row.executivoHf,
        row.regional,
      ].join(' ')),
      regionalKey: normalizeKey(row.regional),
      metaValue: parseMetaNumber(row.metaHpf),
    }));
  }, [rows]);

  const hasActiveFilters = Boolean(
    searchTerm ||
    regionalFilter ||
    statusFilter !== 'all' ||
    minMetaFilter ||
    maxMetaFilter
  );

  const filteredRows = useMemo(() => {
    const term = normalizeKey(searchTerm);
    const regionalKey = normalizeKey(regionalFilter);
    const minMeta = parseMetaNumber(minMetaFilter);
    const maxMeta = parseMetaNumber(maxMetaFilter);

    return indexedRows.filter(({ row, searchKey, regionalKey: rowRegionalKey, metaValue }) => {
      if (term) {
        if (!searchKey.includes(term)) return false;
      }

      if (regionalKey && rowRegionalKey !== regionalKey) return false;

      const isPending = changedGuids.has(row.guid);
      if (statusFilter === 'pending' && !isPending) return false;
      if (statusFilter === 'unchanged' && isPending) return false;

      if (minMeta !== null && (metaValue === null || metaValue < minMeta)) return false;
      if (maxMeta !== null && (metaValue === null || metaValue > maxMeta)) return false;

      return true;
    }).map(({ row }) => row);
  }, [indexedRows, searchTerm, regionalFilter, statusFilter, minMetaFilter, maxMetaFilter, changedGuids]);

  const totalMeta = useMemo(() => {
    return filteredRows.reduce((sum, row) => {
      const value = parseMetaNumber(row.metaHpf);
      return value === null ? sum : sum + value;
    }, 0);
  }, [filteredRows]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredRows.length / pageSize)),
    [filteredRows.length, pageSize]
  );
  const safeCurrentPage = Math.min(currentPage, pageCount);
  const paginatedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, pageSize, safeCurrentPage]);
  const visibleStart = filteredRows.length === 0 ? 0 : ((safeCurrentPage - 1) * pageSize) + 1;
  const visibleEnd = Math.min(safeCurrentPage * pageSize, filteredRows.length);

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleRegionalFilterChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setRegionalFilter(event.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(event.target.value as StatusFilter);
    setCurrentPage(1);
  }, []);

  const handleMinMetaFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMinMetaFilter(sanitizeMetaInput(event.target.value));
    setCurrentPage(1);
  }, []);

  const handleMaxMetaFilterChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxMetaFilter(sanitizeMetaInput(event.target.value));
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((page) => Math.min(pageCount, page + 1));
  }, [pageCount]);

  const saveRows = async (
    nextRows: MetaUnileverRow[],
    source: 'table' | 'form' | 'import',
    action: string,
    options: { ignoreNotFound?: boolean } = {}
  ) => {
    const preparedRows = prepareRowsForSave(nextRows);

    if (preparedRows.length === 0) {
      openNotice({
        title: 'Nenhuma linha para salvar',
        message: 'Inclua pelo menos uma linha valida antes de salvar.',
        variant: 'danger',
      });
      return false;
    }

    setIsSaving(true);
    const response = await saveMetasUnilever(preparedRows, source, action, options.ignoreNotFound === true);
    setIsSaving(false);

    if (!response.success) {
      const skippableErrors = response.skippableErrors || [];
      const validationErrors = response.validationErrors || [];

      if (response.canIgnoreNotFound && skippableErrors.length > 0 && validationErrors.length === 0) {
        openNotice({
          title: 'Registros nao identificados',
          message: `${skippableErrors.length} ${skippableErrors.length === 1 ? 'referencia nao foi encontrada' : 'referencias nao foram encontradas'}. Voce pode cadastrar ignorando essas linhas ou subir um novo arquivo.`,
          details: validationErrorLines(skippableErrors),
          variant: 'confirm',
          confirmLabel: 'Cadastrar ignorando',
          cancelLabel: source === 'import' ? 'Subir novo arquivo' : 'Corrigir dados',
          onCancel: source === 'import'
            ? () => {
              setNotice(null);
              setImportOpen(false);
              setTimeout(() => setImportOpen(true), 0);
            }
            : undefined,
          onConfirm: async () => {
            setNoticeLoading(true);
            const success = await saveRows(nextRows, source, `${action}_ignore_not_found`, { ignoreNotFound: true });
            setNoticeLoading(false);
            if (success && source === 'import') setImportOpen(false);
          },
        });
        return false;
      }

      openNotice({
        title: 'Dados invalidos',
        message: response.error || 'Revise as linhas indicadas e tente novamente.',
        details: validationErrorLines([...validationErrors, ...skippableErrors]),
        variant: 'danger',
      });
      return false;
    }

    const savedRows = hydrateRows(response.data?.rows || preparedRows);
    const skippedCount = response.skippedCount || 0;
    setRows(savedRows);
    setOriginalRows(savedRows);
    openNotice({
      title: skippedCount > 0 ? 'Metas salvas com registros ignorados' : 'Metas salvas',
      message: skippedCount > 0
        ? `${response.changedCount || 0} ${response.changedCount === 1 ? 'registro alterado' : 'registros alterados'} e ${skippedCount} ${skippedCount === 1 ? 'linha ignorada' : 'linhas ignoradas'}. O log de ignorados foi registrado separadamente.`
        : `${response.changedCount || 0} ${response.changedCount === 1 ? 'registro alterado' : 'registros alterados'}.`,
      details: skippedCount > 0 ? skippedRowLines(response.skippedRows) : undefined,
      variant: 'success',
    });
    return true;
  };

  const handleCellChange = useCallback((
    guid: string,
    key: keyof Omit<MetaUnileverRow, 'guid'>,
    value: string
  ) => {
    setRows((currentRows) => {
      let changed = false;
      const nextRows = currentRows.map((row) => {
        if (row.guid !== guid) return row;
        if (String(row[key] ?? '') === String(value ?? '')) return row;

        changed = true;
        return { ...row, [key]: value };
      });

      return changed ? nextRows : currentRows;
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setRows((currentRows) => {
      const nextRows = [...currentRows, createEmptyRow()];
      setCurrentPage(Math.max(1, Math.ceil(nextRows.length / pageSize)));
      return nextRows;
    });
  }, [pageSize]);

  const handleRemoveRow = useCallback((guid: string) => {
    const row = rowsRef.current.find((item) => item.guid === guid);
    const isPersisted = originalGuidSet.has(guid);

    if (!isPersisted) {
      setRows((currentRows) => currentRows.filter((item) => item.guid !== guid));
      return;
    }

    openNotice({
      title: 'Remover linha',
      message: `A linha de ${row?.distribuidor || 'metas'} ficara pendente ate salvar as alteracoes.`,
      variant: 'confirm',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
      onConfirm: () => {
        setRows((currentRows) => currentRows.filter((item) => item.guid !== guid));
        setNotice(null);
      },
    });
  }, [openNotice, originalGuidSet]);

  const handleTableSave = () => {
    if (pendingCount === 0) return;

    openNotice({
      title: 'Salvar alteracoes',
      message: `${pendingCount} ${pendingCount === 1 ? 'linha pendente' : 'linhas pendentes'} serao validadas antes de salvar.`,
      variant: 'confirm',
      confirmLabel: 'Salvar',
      cancelLabel: 'Cancelar',
      onConfirm: async () => {
        setNoticeLoading(true);
        await saveRows(rows, 'table', 'table_save');
        setNoticeLoading(false);
      },
    });
  };

  const handleFormSubmit = async (row: MetaUnileverRow) => {
    const nextRows = mergeRowsByDistributor(rows, [row]);
    const success = await saveRows(nextRows, 'form', 'form_insert');
    if (success) setFormOpen(false);
  };

  const handleImportRows = async (importedRows: MetaUnileverRow[]) => {
    const nextRows = mergeRowsByDistributor(rows, importedRows);
    return saveRows(nextRows, 'import', 'xlsx_import');
  };

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setRegionalFilter('');
    setStatusFilter('all');
    setMinMetaFilter('');
    setMaxMetaFilter('');
    setCurrentPage(1);
  }, []);

  const handleExportRows = () => {
    const rowsToExport = filteredRows;

    if (rowsToExport.length === 0) {
      openNotice({
        title: 'Nada para exportar',
        message: 'Nenhum registro foi encontrado com os filtros atuais.',
        variant: 'danger',
      });
      return;
    }

    try {
      const sheetRows = [
        EXPECTED_HEADERS,
        ...rowsToExport.map((row) => {
          const metaValue = parseMetaNumber(row.metaHpf);

          return [
            row.distribuidor || '',
            row.consultor || '',
            row.coordenador || '',
            row.executivoBpc || '',
            row.executivoHf || '',
            row.regional || '',
            metaValue === null ? '' : metaValue,
          ];
        }),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
      worksheet['!cols'] = [
        { wch: 34 },
        { wch: 24 },
        { wch: 24 },
        { wch: 24 },
        { wch: 24 },
        { wch: 18 },
        { wch: 16 },
      ];

      for (let rowIndex = 1; rowIndex < sheetRows.length; rowIndex += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: 6 });
        const cell = worksheet[cellAddress];
        if (cell && typeof cell.v === 'number') {
          cell.t = 'n';
          cell.z = '"R$" #,##0.00';
        }
      }

      worksheet['!autofilter'] = {
        ref: XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: sheetRows.length - 1, c: EXPECTED_HEADERS.length - 1 },
        }),
      };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'metas PBI Gustavo');
      XLSX.writeFile(workbook, `metas-unilever-${new Date().toISOString().slice(0, 10)}.xlsx`);

      openNotice({
        title: 'XLSX exportado',
        message: `${rowsToExport.length} ${rowsToExport.length === 1 ? 'registro exportado' : 'registros exportados'} no padrao de importacao.`,
        variant: 'success',
      });
    } catch {
      openNotice({
        title: 'Nao foi possivel exportar',
        message: 'Tente novamente em alguns instantes.',
        variant: 'danger',
      });
    }
  };

  const handleOpenHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const historyRows = await fetchMetasUnileverHistory();
      setHistory(historyRows);
    } catch {
      setHistory([]);
      openNotice({
        title: 'Nao foi possivel carregar o historico',
        message: 'Tente novamente em alguns instantes.',
        variant: 'danger',
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <>
      <PageTitle>Data Transfer - Metas Unilever</PageTitle>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <header className="mb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">Metas Unilever</h1>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={loadRows}
                  disabled={isLoading}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  aria-label="Recarregar"
                  title="Recarregar"
                >
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Inserir linha
                </button>
                <button
                  type="button"
                  onClick={() => setFormOpen(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <UserPlus className="h-4 w-4" />
                  Formulario
                </button>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <Upload className="h-4 w-4" />
                  Importar XLSX
                </button>
                <button
                  type="button"
                  onClick={handleExportRows}
                  disabled={filteredRows.length === 0}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Exportar XLSX
                </button>
                <button
                  type="button"
                  onClick={handleOpenHistory}
                  className="inline-flex h-10 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <History className="h-4 w-4" />
                  Historico
                </button>
                <button
                  type="button"
                  onClick={handleTableSave}
                  disabled={pendingCount === 0 || isSaving}
                  className="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Salvar alteracoes
                </button>
              </div>
            </div>
          </header>

          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-2">
                  <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">
                    {filteredRows.length}
                    {hasActiveFilters && <span className="text-sm font-semibold text-gray-400"> / {rows.length}</span>}
                  </p>
                  <p className="text-xs text-gray-500">{hasActiveFilters ? 'Linhas filtradas' : 'Linhas cadastradas'}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-2">
                  <Save className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{pendingCount}</p>
                  <p className="text-xs text-gray-500">Pendentes</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-emerald-50 p-2">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{formatCurrencyBRL(totalMeta)}</p>
                  <p className="text-xs text-gray-500">Total Meta HPF</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-gray-100 p-2">
                  <Filter className="h-4 w-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Filtros</p>
                  <p className="text-xs text-gray-500">{filteredRows.length} de {rows.length} registros</p>
                </div>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <XCircle className="h-3.5 w-3.5" />
                Limpar filtros
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1.5fr)_minmax(180px,1fr)_minmax(170px,0.8fr)_minmax(140px,0.7fr)_minmax(140px,0.7fr)]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Buscar distribuidor, consultor, executivo..."
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <select
                value={regionalFilter}
                onChange={handleRegionalFilterChange}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Todas as regionais</option>
                {regionalOptions.map((regional) => (
                  <option key={regional} value={regional}>{regional}</option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="all">Todos os status</option>
                <option value="pending">Com pendencia</option>
                <option value="unchanged">Sem pendencia</option>
              </select>

              <input
                type="text"
                inputMode="decimal"
                value={minMetaFilter}
                onChange={handleMinMetaFilterChange}
                placeholder="Meta min."
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              />

              <input
                type="text"
                inputMode="decimal"
                value={maxMetaFilter}
                onChange={handleMaxMetaFilterChange}
                placeholder="Meta max."
                className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-16 shadow-sm">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Carregando metas...</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <MetasUnileverTable
                rows={paginatedRows}
                changedGuids={changedGuids}
                onChange={handleCellChange}
                onRemove={handleRemoveRow}
              />
              {filteredRows.length > 0 && (
                <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-gray-500">
                    Mostrando {visibleStart}-{visibleEnd} de {filteredRows.length} registros filtrados
                  </p>

                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                      Linhas por pagina
                      <select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                        <option value={500}>500</option>
                      </select>
                    </label>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={goToPreviousPage}
                        disabled={safeCurrentPage <= 1}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Pagina anterior"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="min-w-[88px] text-center text-xs font-semibold text-gray-600">
                        {safeCurrentPage} de {pageCount}
                      </span>
                      <button
                        type="button"
                        onClick={goToNextPage}
                        disabled={safeCurrentPage >= pageCount}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Proxima pagina"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <FormModal
        isOpen={formOpen}
        loading={isSaving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImportRows}
      />

      <HistoryModal
        isOpen={historyOpen}
        loading={historyLoading}
        history={history}
        onClose={() => setHistoryOpen(false)}
      />

      <NoticeModal
        isOpen={notice !== null}
        title={notice?.title || ''}
        message={notice?.message}
        details={notice?.details}
        variant={notice?.variant}
        confirmLabel={notice?.confirmLabel}
        cancelLabel={notice?.cancelLabel}
        loading={noticeLoading || isSaving}
        onConfirm={notice?.onConfirm}
        onCancel={notice?.onCancel}
        onClose={closeNotice}
      />
    </>
  );
};

export default MetasUnileverScreen;
